import { useCallback, useEffect, useState } from "react";
import { AlertCircle, Banknote } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { fetchChaoUnitAvgValue, saveChaoUnitAvgValue } from "@/services/chaoDeObra";
import { formatCurrency } from "@/utils/format";

interface Props {
  open: boolean;
  onClose: () => void;
  costCenterId: string;
  costCenterLabel: string;
  unitCount: number;
  onSaved: (value: number) => void;
}

function parseBrlInput(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, "").replace(/R\$/gi, "").replace(/\./g, "").replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function ChaoValorMedioUnidadeModal({
  open,
  onClose,
  costCenterId,
  costCenterLabel,
  unitCount,
  onSaved,
}: Props) {
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!costCenterId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchChaoUnitAvgValue(costCenterId);
      if (res.success && res.value && res.value > 0) {
        setDraft(
          res.value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        );
      } else {
        setDraft("");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar o valor.");
      setDraft("");
    } finally {
      setLoading(false);
    }
  }, [costCenterId]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  const parsed = parseBrlInput(draft);
  const valorTotal = parsed && unitCount > 0 ? parsed * unitCount : 0;

  const onSave = async () => {
    const value = parseBrlInput(draft);
    if (value == null) {
      setError("Informe um valor médio maior que zero.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await saveChaoUnitAvgValue(costCenterId, value);
      if (!res.success) {
        setError(res.error ?? "Falha ao salvar.");
        return;
      }
      onSaved(value);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Valor médio da unidade de imóvel"
      size="md"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">
          Obra: <strong>{costCenterLabel}</strong>
          <br />
          <span className="text-slate-500">
            Este valor multiplica a quantidade de unidades de imóvel ({unitCount}) para formar a base do{" "}
            <strong>avanço financeiro</strong> (custo Gerencial ÷ valor total das unidades).
          </span>
        </p>

        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Valor médio por unidade (R$)
          </span>
          <div className="mt-1 relative">
            <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              inputMode="decimal"
              className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              placeholder="0,00"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={loading || saving}
            />
          </div>
        </label>

        {parsed != null && unitCount > 0 && (
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            Valor total das unidades: <strong>{formatCurrency(valorTotal)}</strong> ({unitCount} ×{" "}
            {formatCurrency(parsed)})
          </p>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void onSave()} loading={saving} disabled={loading}>
            Salvar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
